import { ApolloClient } from '@apollo/client';
import { PropertyType } from '../../domain/propertyTypes';
import {
  Content,
  fetchPosts,
  fetchPostsWithAds,
  fetchVideos,
} from './fetchPostsData';
import {
  getSubcategories,
  PostMainCategory,
  PostSubcategory,
} from '../../domain/postCategories';
import { fifthInEverySix } from '../../ads/adPlacementLogic';
import { resolved } from '../../utility/promiseHelper';
import { fetchArticleBlock } from '../../../../services/post';

export const fetchPropertyTypeLandingPageData = async (
  client: ApolloClient<unknown>,
  excludePostIds: number[],
  propertyType: PropertyType,
) => {
  const homePageDataRequirement = {
    review: 6,
    preview: 3,
    pov: 3,
    news: 3,
    guides: 3,
    decor: 3,
    heroVideo: 1,
    featuredVideos: 3,
  };

  const propertyTypeLandingPageDataRequirement = (
    propertyType: PropertyType,
  ) => ({
    review: 6,
    preview: 3,
    pov: isNotShowingPovContent(propertyType) ? 0 : 3,
    news: 0,
    guides: 0,
    decor: 0,
    heroVideo: 1,
    featuredVideos: 3,
  });

  const isHomePage = !propertyType;
  const dataRequirement = !isHomePage
    ? propertyTypeLandingPageDataRequirement(propertyType)
    : homePageDataRequirement;

  const [
    reviewData,
    previewData,
    povData,
    newsData,
    guidesData,
    decorData,
    videoData,
    editorsPickData,
  ] = await Promise.allSettled([
    fetchPostsWithAds(
      client,
      dataRequirement.review,
      0,
      {
        postCategory: [PostSubcategory.Review],
        ...(propertyType ? { propertyType } : {}),
      },
      fifthInEverySix,
      excludePostIds,
    ),
    fetchPosts(
      client,
      dataRequirement.preview,
      0,
      {
        postCategory: [PostSubcategory.Preview],
        ...(propertyType ? { propertyType } : {}),
      },
      excludePostIds,
    ),
    fetchPosts(
      client,
      dataRequirement.pov,
      0,
      {
        postCategory: [PostSubcategory.PointOfView],
        ...(propertyType ? { propertyType } : {}),
      },
      excludePostIds,
    ),
    fetchPosts(
      client,
      dataRequirement.news,
      0,
      {
        postCategory: [...getSubcategories(PostMainCategory.News)],
        ...(propertyType ? { propertyType } : {}),
      },
      excludePostIds,
    ),
    fetchPosts(
      client,
      dataRequirement.guides,
      0,
      {
        postCategory: [...getSubcategories(PostMainCategory.Guides)],
        ...(propertyType ? { propertyType } : {}),
      },
      excludePostIds,
    ),
    fetchPosts(
      client,
      dataRequirement.decor,
      0,
      {
        postCategory: [...getSubcategories(PostMainCategory.Decor)],
        ...(propertyType ? { propertyType } : {}),
      },
      excludePostIds,
    ),
    fetchVideos(client, dataRequirement.featuredVideos, {
      propertyType,
    }),
    fetchArticleBlock(client),
  ]);

  const defaultContents = { contents: [] as Content[], hasMore: false };

  return {
    review: resolved(reviewData, defaultContents).contents,
    preview: resolved(previewData, defaultContents).contents,
    pov: resolved(povData, defaultContents).contents,
    news: resolved(newsData, defaultContents).contents,
    guides: resolved(guidesData, defaultContents).contents,
    decor: resolved(decorData, defaultContents).contents,
    video: resolved(videoData, { featuredVideos: [], heroVideo: null }),
    editorsPicks: resolved(editorsPickData, []),
  };
};
export const isNotShowingPovContent = (propertyType: PropertyType) => {
  return (
    propertyType === PropertyType.Townhome ||
    propertyType === PropertyType.HolidayHomes ||
    propertyType === PropertyType.HomeOffice ||
    propertyType === PropertyType.MixedUse ||
    propertyType === PropertyType.Commercial
  );
};
